import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { portalConst } from '@app/config';
import { safeEmpty } from '@app/shared/utils';
import { Observable, switchMap } from 'rxjs';
import { ApiService, Resp } from './api.service';

export interface PresignedDownloadUrlResponse {
  download_url: string;
  title: string;
}

export interface PresignedUrlResponse {
  upload_url: string;
  key: string;
  file: string;
}

export interface MultipartInitResponse {
  upload_id: string;
  key: string;
  file: string;
  parts: { part_number: number; upload_url: string }[];
}

export interface MultipartCompleteResponse {
  location: string;
  key: string;
  bucket: string;
}

@Injectable({ providedIn: 'root' })
export class AttachmentService {
  private readonly _apiService = inject(ApiService);
  private readonly _url = `${portalConst.api.eclass}/v2/attachments`;

  public getPresignedUrl(filename: string, mimeType: string, folder: string): Observable<Resp<PresignedUrlResponse>> {
    return this._apiService.post<PresignedUrlResponse>(
      `${this._url}/upload/presigned-url`,
      {},
      {
        filename,
        mime_type: mimeType || 'application/octet-stream',
        folder,
      },
    );
  }

  public getPresignedDownloadUrl(attachment_id: number): Observable<Resp<PresignedDownloadUrlResponse>> {
    return this._apiService.get<PresignedDownloadUrlResponse>(`${this._url}/download/presigned-url`, { attachment_id });
  }

  public uploadToS3(
    file: File,
    uploadUrl: string,
    key: string,
    returnedFile: string,
  ): Observable<{ progress: number } | { done: true; key: string; file: string }> {
    return new Observable((observer) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          observer.next({ progress });
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          observer.next({ done: true as const, key, file: returnedFile });
          observer.complete();
        } else {
          observer.error(new Error(`S3 retornou status ${xhr.status}`));
        }
      };

      xhr.onerror = () => observer.error(new Error('Erro de rede ao enviar para o S3'));

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);

      return () => {
        if (xhr.readyState !== XMLHttpRequest.DONE) {
          xhr.abort();
        }
      };
    });
  }

  public simpleUpload(
    file: File,
    folder: string,
  ): Observable<{ progress: number } | { done: true; key: string; file: string }> {
    return this.getPresignedUrl(file.name, file.type || 'application/octet-stream', folder).pipe(
      safeEmpty(),
      switchMap(({ upload_url, key, file: returnedFile }) => this.uploadToS3(file, upload_url, key, returnedFile)),
    );
  }

  public initMultipartUpload(
    filename: string,
    mimeType: string,
    folder: string,
    totalParts: number,
  ): Observable<Resp<MultipartInitResponse>> {
    return this._apiService.post<MultipartInitResponse>(
      `${this._url}/upload/multipart/init`,
      {},
      {
        filename,
        mime_type: mimeType,
        folder,
        total_parts: totalParts,
      },
    );
  }

  public async uploadPart(uploadUrl: string, blob: Blob, signal?: AbortSignal): Promise<string> {
    const response = await fetch(uploadUrl, { method: 'PUT', body: blob, signal });
    if (!response.ok) {
      throw new Error(`Part upload falhou: ${response.status}`);
    }
    return response.headers.get('ETag') ?? '';
  }

  public completeMultipartUpload(
    key: string,
    uploadId: string,
    parts: { part_number: number; etag: string }[],
  ): Observable<Resp<MultipartCompleteResponse>> {
    return this._apiService.post<MultipartCompleteResponse>(
      `${this._url}/upload/multipart/complete`,
      {},
      {
        key,
        upload_id: uploadId,
        parts: [...parts].sort((a, b) => a.part_number - b.part_number),
      },
    );
  }

  public abortMultipartUpload(key: string, uploadId: string): Observable<Resp<void>> {
    return this._apiService.delete(`${this._url}/upload/multipart/abort`, { key, upload_id: uploadId });
  }

  public deleteAttachment(id: number): Observable<Resp<any>> {
    return this._apiService.delete(`${portalConst.api.eclass}/v1/attachments/${id}`);
  }

  public checkVideo(req: any): Observable<any> {
    return this._apiService.post(`${portalConst.api.eclass}/v1/video-hash`, req);
  }
}
